import { Box } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import { Typography } from "@mui/material";
import {
  SideBySideComparisonSectionView,
  SideBySideComparisonSectionConfig,
} from "@cma/presentation-generation-logic";

const Container = styled(Box)`
  padding: 1rem;
  width: 100%;
  margin: auto;
  min-width: 0;

  ${(p) => p.theme.media.mobileDown} {
    width: 160px;
  }
`;

const LabelPhotoPlaceholder = styled(Box)`
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  margin-bottom: 1rem;
  background-color: transparent;
`;

const ContentContainer = styled(Box)`
  display: flex;
  width: 100%;
  text-align: left;
  flex-direction: column;
  gap: 0.75rem;
`;

const AddressLabelBox = styled(Box)`
  min-height: 2.75rem;
`;

interface Props {
  preferences?: SideBySideComparisonSectionView["preferences"];
}

export const ComparisonPropertyItemLabels = (props: Props) => {
  const { preferences } = props;
  const theme = useTheme();

  // Type-safe field labels that must match ComparisonPropertyItem field checks
  const fieldLabels = [
    {
      key: "showPropertyType" as const,
      label: "Property Type",
    },
    {
      key: "showListingStatus" as const,
      label: "Status",
    },
    {
      key: "showListPrice" as const,
      label: "List Price",
    },
    {
      key: "showListDate" as const,
      label: "List Date",
    },
    {
      key: "showSoldPrice" as const,
      label: "Sold Price",
    },
    {
      key: "showSoldDate" as const,
      label: "Sold Date",
    },
    {
      key: "showYearBuilt" as const,
      label: "Year Built",
    },
    {
      key: "showNeighborhood" as const,
      label: "Neighborhood",
    },
    {
      key: "showLivingSpace" as const,
      label: "Living Area",
    },
    {
      key: "showBeds" as const,
      label: "Beds",
    },
    {
      key: "showBaths" as const,
      label: "Baths",
    },
    {
      key: "showLotSize" as const,
      label: "Lot Size",
    },
    {
      key: "showHOAFees" as const,
      label: "HOA Fees",
    },
    {
      key: "showGarageParkingSpaces" as const,
      label: "Parking",
    },
    {
      key: "showPricePerLivingSpaceSqft" as const,
      label: "Price per Sqft",
    },
    {
      key: "showRoomsTotal" as const,
      label: "Total Rooms",
    },
    {
      key: "showDoormanAttendance" as const,
      label: "Doorman Attendance",
    },
    {
      key: "showRecurringCosts" as const,
      label: "Recurring Costs",
    },
    {
      key: "showFlipTaxRemarks" as const,
      label: "Flip Tax Remarks",
    },
    {
      key: "showDaysOnMarket" as const,
      label: "Days on Market",
    },
    {
      key: "showCumulativeDaysOnMarket" as const,
      label: "Cumulative Days on Market",
    },
  ] satisfies Array<{
    key: keyof SideBySideComparisonSectionConfig;
    label: string;
  }>;

  const visibleLabels = fieldLabels.filter((item) => {
    const value = preferences?.[item.key];
    return value !== false;
  });

  return (
    <Container sx={{ background: theme.colors.background }}>
      <LabelPhotoPlaceholder />
      <ContentContainer>
        <AddressLabelBox>
          <Typography fontWeight={700} textAlign={"right"}>
            Address
          </Typography>
        </AddressLabelBox>
        {visibleLabels.map((item) => (
          <Box key={item.key}>
            <Typography fontWeight={700} textAlign={"right"}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </ContentContainer>
    </Container>
  );
};
